import React from 'react';
import { Box, Typography, Stack, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CalendarMonth as CalendarIcon,
  Insights as InsightsIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';

import { LogoMark } from '../Common/Logo';

const VALUE_PROPS = [
  { icon: <CalendarIcon fontSize="small" />, text: 'Plan and schedule across every platform from one calendar' },
  { icon: <InsightsIcon fontSize="small" />, text: 'Real analytics, not vanity dashboards' },
  { icon: <GroupsIcon fontSize="small" />, text: 'Built for agencies managing multiple brands' },
];

const AuthLayout = ({ children }) => {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* Brand panel — hidden on small screens */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          width: '42%',
          minWidth: 420,
          p: 6,
          backgroundColor: '#12131C',
          color: '#EDEFF6',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 20% 20%, rgba(129,140,248,0.16), transparent 55%)',
            pointerEvents: 'none',
          }}
        />
        <Link component={RouterLink} to="/" underline="none" color="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, position: 'relative' }}>
          <LogoMark size={30} />
          <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
            Mom N Kidz SocialFlow
          </Typography>
        </Link>

        <Box sx={{ position: 'relative' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, maxWidth: 340, lineHeight: 1.25 }}>
            Every brand, every platform, one workspace.
          </Typography>
          <Stack spacing={2}>
            {VALUE_PROPS.map((item) => (
              <Box key={item.text} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(129,140,248,0.16)',
                    color: '#A5ABF6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(237,239,246,0.75)', pt: 0.5 }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Typography variant="caption" sx={{ color: 'rgba(237,239,246,0.4)', position: 'relative' }}>
          © {new Date().getFullYear()} Mom N Kidz SocialFlow. All rights reserved.
        </Typography>
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          backgroundColor: 'background.default',
        }}
      >
        <Link component={RouterLink} to="/" underline="none" color="inherit" sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, position: 'absolute', top: 24, left: 24 }}>
          <LogoMark size={26} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Mom N Kidz SocialFlow</Typography>
        </Link>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ width: '100%', maxWidth: 400 }}
        >
          {children}
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 4, flexWrap: 'wrap' }}>
            <Link component={RouterLink} to="/terms" variant="caption" color="text.secondary">Terms of Service</Link>
            <Link component={RouterLink} to="/privacy" variant="caption" color="text.secondary">Privacy Policy</Link>
            <Link component={RouterLink} to="/data-deletion" variant="caption" color="text.secondary">Data Deletion</Link>
          </Stack>
        </motion.div>
      </Box>
    </Box>
  );
};

export default AuthLayout;
